import { PreviewAnomaly } from '@api/twin/AnomalyApi';
import { PencilIcon } from '@heroicons/react/24/outline';
import { Icon } from '@stereograph/teia-system-design/components/Icon';
import { IconButton } from '@stereograph/teia-system-design/components/IconButton';
import { CircularProgress } from '@stereograph/teia-system-design/components/Progress';
import { Tooltip } from '@stereograph/teia-system-design/components/Tooltip';
import { usePluginTranslation } from '@translation';
import { MouseEvent, useState } from 'react';
import { useAnomalyTabContext } from '../../context/AnomalyTabsContext';
import { useEnsureAnomaly, useHasCachedAnomaly, usePrefetchAnomaly } from '../../hooks/useAnomalyQueryOptions';

interface EditAnomalyButton {
  previewAnomaly: PreviewAnomaly;
}

export const EditAnomalyButton = (props: EditAnomalyButton) => {
  const { previewAnomaly } = props;
  const { t } = usePluginTranslation();
  const { handleAddTabClick, createAnomalyTab } = useAnomalyTabContext();
  const prefetch = usePrefetchAnomaly(previewAnomaly.projectId, previewAnomaly.id);
  const hasCachedData = useHasCachedAnomaly(previewAnomaly.projectId, previewAnomaly.id);
  const ensureAnomaly = useEnsureAnomaly(previewAnomaly.projectId, previewAnomaly.id);

  const [openTip, setOpenTip] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const onClick = async (event: MouseEvent) => {
    try {
      if (!hasCachedData) {
        setIsBusy(true);
      }

      const ensureAnomalyData = await ensureAnomaly();
      if (!ensureAnomalyData?.stateByGuid) {
        return;
      }

      handleAddTabClick(event, createAnomalyTab(ensureAnomalyData, true));
      setOpenTip(false);
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Tooltip
      title={t('anomaly_plugin.resume_widget.table.columns.actions.edit_tooltip')}
      placement="top"
      open={openTip}
      onClose={() => setOpenTip(false)}
      onOpen={() => setOpenTip(true)}
    >
      <IconButton
        onClick={onClick}
        onPointerEnter={() => {
          setOpenTip(true);
          prefetch();
        }}
        aria-label={t('anomaly_plugin.resume_widget.table.columns.actions.edit_tooltip')}
        disabled={isBusy}
        iconOnly
        data-dd-action-name="tvp.anomaly.edit"
      >
        {isBusy
          ? <CircularProgress size={20} />
          : (
            <Icon
              component={PencilIcon}
              sx={{
                color: (theme) => theme.palette.primary.main,
              }}
            />
          )}
      </IconButton>
    </Tooltip>
  );
};
